import { useRef, useState } from "react";
import { Megaphone, Bot, BarChart3, Video, CalendarDays, Mic, Volume2, Loader2 } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";

/* ─── Voiceover play button ─────────────────────────────────────────────────── */
type PlayState = "idle" | "loading" | "playing";

function useOrbVoiceover() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioBlobUrlRef = useRef<string | null>(null);
  const [state, setState] = useState<PlayState>("idle");

  const play = async () => {
    if (state === "loading") return;

    // If already loaded, just replay
    if (audioBlobUrlRef.current) {
      audioRef.current?.play();
      setState("playing");
      return;
    }

    setState("loading");
    try {
      const { data, error } = await supabase.functions.invoke("landing-voiceover");
      if (error || !data?.audio) throw new Error(error?.message ?? "No audio returned");

      const binary = atob(data.audio);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      audioBlobUrlRef.current = url;

      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("idle");
      await audio.play();
      setState("playing");
    } catch (e) {
      console.error("Voiceover error", e);
      setState("idle");
    }
  };

  return { state, play };
}

/* ─── Metallic orb ─────────────────────────────────────────────────────────── */
function MetallicOrb() {
  const { state, play } = useOrbVoiceover();

  return (
    <div className="relative flex flex-col items-center gap-4">
    <div className="relative flex items-center justify-center" style={{ width: 220, height: 220 }}>
      {/* Outer halo */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          boxShadow: "0 0 80px 30px rgba(14,165,233,0.30), 0 0 160px 60px rgba(14,165,233,0.10)",
          borderRadius: "50%",
        }}
      />
      {/* Ambient ring */}
      <div
        className="absolute rounded-full animate-pulse-slow"
        style={{
          width: 260, height: 260,
          background: "radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)",
          top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        }}
      />
      {/* Orb body */}
      <div
        className="relative rounded-full overflow-hidden"
        style={{
          width: 200, height: 200,
          background: "conic-gradient(from 0deg, #0c2a4a, #0369a1, #0ea5e9, #38bdf8, #0284c7, #051a35, #0c2a4a, #0369a1, #0ea5e9, #0c2a4a)",
          boxShadow: "inset 0 0 40px rgba(0,0,0,0.6), inset 0 8px 20px rgba(255,255,255,0.06)",
          animation: "orb-spin 12s linear infinite",
        }}
      >
        {/* Specular highlight */}
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 35% 28%, rgba(255,255,255,0.22) 0%, transparent 55%)",
          }}
        />
        {/* Bottom shadow */}
        <div
          className="absolute inset-0"
          style={{
            background: "radial-gradient(ellipse at 65% 75%, rgba(0,0,0,0.45) 0%, transparent 55%)",
          }}
        />
      </div>
    </div>

    {/* Voiceover play button */}
    <button
      type="button"
      onClick={play}
      disabled={state === "loading"}
      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
      style={{
        backgroundColor: state === "playing" ? "rgba(14,165,233,0.22)" : "rgba(14,165,233,0.10)",
        border: `1px solid ${state === "playing" ? "rgba(14,165,233,0.6)" : "rgba(14,165,233,0.3)"}`,
        color: "#38bdf8",
        boxShadow: state === "playing" ? "0 0 20px rgba(14,165,233,0.25)" : "none",
      }}
    >
      {state === "loading" ? (
        <Loader2 size={14} className="animate-spin" />
      ) : (
        <Volume2 size={14} />
      )}
      {state === "loading" ? "Loading…" : state === "playing" ? "Playing…" : "Hear AdKavi"}
    </button>
    </div>
  );
}

/* ─── Demo panels ───────────────────────────────────────────────────────────── */

function AdsDemo() {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        One-click multi-platform launch
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[
          { name: "Meta", color: "#1877f2", letter: "f" },
          { name: "TikTok", color: "#010101", letter: "T" },
          { name: "Google", color: "#4285f4", letter: "G" },
          { name: "YouTube", color: "#ff0000", letter: "▶" },
        ].map((p) => (
          <div
            key={p.name}
            className="flex items-center gap-2 px-3 py-2 rounded-xl"
            style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ backgroundColor: p.color }}
            >
              {p.letter}
            </span>
            <span className="text-xs font-medium text-white">{p.name}</span>
            <span className="ml-auto w-2 h-2 rounded-full bg-green-400" />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-1">
        <div className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#f97316", border: "1px solid rgba(249,115,22,0.3)" }}>
          Budget: $50/day
        </div>
        <div className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(14,165,233,0.12)", color: "#38bdf8", border: "1px solid rgba(14,165,233,0.25)" }}>
          Audience: AI-optimized
        </div>
      </div>
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl mt-1" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(249,115,22,0.3)" }}>
        <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
        <span className="text-xs text-white">AI writing your ad copy…</span>
      </div>
    </div>
  );
}

function AgentDemo() {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        AI responds to every lead
      </div>
      <div className="flex flex-col gap-2">
        <div className="self-end max-w-[80%] px-3 py-2 rounded-2xl rounded-tr-sm text-xs text-white" style={{ backgroundColor: "#163554" }}>
          Hi, I saw your ad — how much do your services cost?
        </div>
        <div className="flex gap-2 items-end">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#0ea5e9" }}>
            <Bot size={14} className="text-white" />
          </div>
          <div className="max-w-[80%] px-3 py-2 rounded-2xl rounded-tl-sm text-xs" style={{ backgroundColor: "#0c2a4a", color: "#e2f3fc", border: "1px solid rgba(14,165,233,0.25)" }}>
            Hi! Thanks for reaching out. Our Launch plan is $149/mo and includes everything you need to launch ads across Meta, TikTok, and Google. Want me to send you a quick breakdown?
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#0ea5e9" }}>
            <Bot size={14} className="text-white" />
          </div>
          <div className="flex gap-1 px-3 py-2 rounded-2xl" style={{ backgroundColor: "#0c2a4a", border: "1px solid rgba(14,165,233,0.25)" }}>
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-bounce" style={{ animationDelay: "300ms" }} />
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-1">
        {["WhatsApp", "Instagram", "Messenger"].map((ch) => (
          <div key={ch} className="px-2 py-1 rounded-full text-xs" style={{ backgroundColor: "rgba(14,165,233,0.10)", color: "#7dd3fc", border: "1px solid rgba(14,165,233,0.2)" }}>
            {ch}
          </div>
        ))}
      </div>
    </div>
  );
}

function DashboardDemo() {
  const stats = [
    { label: "ROAS", value: "4.8x", up: true },
    { label: "Leads", value: "1,238", up: true },
    { label: "CPL", value: "$10.37", up: false },
    { label: "Spend", value: "$12.8k", up: true },
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        Live campaign metrics
      </div>
      <div className="grid grid-cols-2 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="p-3 rounded-xl" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)" }}>
            <p className="text-xs text-muted-foreground mb-1">{s.label}</p>
            <p className="text-lg font-bold text-white">{s.value}</p>
            <p className={`text-xs font-medium ${s.up ? "text-green-400" : "text-red-400"}`}>{s.up ? "▲" : "▼"} vs last month</p>
          </div>
        ))}
      </div>
      <div className="flex items-end gap-1 h-12 px-2 py-1 rounded-xl" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)" }}>
        {[40, 60, 50, 75, 65, 85, 70, 90, 78, 95, 82, 100].map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t"
            style={{ height: `${h}%`, background: "linear-gradient(to top, #0369a1, #38bdf8)" }}
          />
        ))}
      </div>
    </div>
  );
}

function VideoDemo() {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        AI generates your video ad
      </div>
      <div className="relative rounded-2xl overflow-hidden" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)", aspectRatio: "16/9" }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(14,165,233,0.2)", border: "2px solid rgba(14,165,233,0.5)" }}>
            <div className="w-0 h-0 ml-1" style={{ borderTop: "8px solid transparent", borderBottom: "8px solid transparent", borderLeft: "14px solid #38bdf8" }} />
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(14,165,233,0.06), transparent 70%)" }} />
      </div>
      <div className="flex gap-2 flex-wrap">
        {["Hook", "Value", "CTA"].map((step, i) => (
          <div key={step} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs" style={{ backgroundColor: i === 0 ? "rgba(14,165,233,0.2)" : "#0e2058", color: i === 0 ? "#38bdf8" : "#64748b", border: `1px solid ${i === 0 ? "rgba(14,165,233,0.4)" : "rgba(255,255,255,0.08)"}` }}>
            <span className="w-4 h-4 rounded-full text-center text-xs font-bold flex items-center justify-center" style={{ backgroundColor: i === 0 ? "#0ea5e9" : "#163554", color: "white" }}>{i + 1}</span>
            {step}
          </div>
        ))}
        <div className="px-2.5 py-1 rounded-full text-xs ml-auto" style={{ backgroundColor: "rgba(249,115,22,0.12)", color: "#f97316", border: "1px solid rgba(249,115,22,0.25)" }}>
          Generating…
        </div>
      </div>
      <div className="text-xs" style={{ color: "#64748b" }}>Script → Voiceover → Visuals → Done in under 2 min</div>
    </div>
  );
}

function VoiceDemo() {
  const bars = [3, 6, 9, 12, 8, 14, 10, 5, 12, 9, 7, 11, 6, 13, 8, 4, 10, 7, 12, 9];
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        Clone your voice in minutes
      </div>
      <div className="p-4 rounded-2xl" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(14,165,233,0.15)", border: "1px solid rgba(14,165,233,0.3)" }}>
            <Mic size={16} style={{ color: "#0ea5e9" }} />
          </div>
          <div>
            <p className="text-xs font-semibold text-white">Your Voice Clone</p>
            <p className="text-xs" style={{ color: "#64748b" }}>Ready · 98% match</p>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-green-400">Live</span>
          </div>
        </div>
        {/* Waveform */}
        <div className="flex items-center gap-0.5 h-10">
          {bars.map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-full"
              style={{
                height: `${h * 6}%`,
                background: "linear-gradient(to top, #0369a1, #38bdf8)",
                opacity: 0.7 + (i % 3) * 0.1,
              }}
            />
          ))}
        </div>
      </div>
      <div className="text-xs" style={{ color: "#64748b" }}>Record 30 seconds → AdKavi uses your voice in every future ad</div>
      <div className="flex gap-2">
        <div className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(14,165,233,0.12)", color: "#38bdf8", border: "1px solid rgba(14,165,233,0.25)" }}>
          Natural intonation
        </div>
        <div className="px-3 py-1.5 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(14,165,233,0.12)", color: "#38bdf8", border: "1px solid rgba(14,165,233,0.25)" }}>
          Multi-language
        </div>
      </div>
    </div>
  );
}

function ScheduleDemo() {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const posts: Record<number, string> = { 1: "#f97316", 3: "#0ea5e9", 5: "#a855f7", 8: "#f97316", 10: "#0ea5e9", 12: "#22c55e", 15: "#f97316", 17: "#0ea5e9" };
  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "#0ea5e9" }}>
        AI schedules & posts for you
      </div>
      <div className="p-3 rounded-2xl" style={{ backgroundColor: "#0e2058", border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {days.map((d) => (
            <div key={d} className="text-center text-xs font-medium" style={{ color: "#64748b" }}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 21 }, (_, i) => (
            <div
              key={i}
              className="rounded-lg aspect-square flex items-center justify-center text-xs"
              style={{
                backgroundColor: posts[i] ? posts[i] + "22" : "#0a1425",
                border: `1px solid ${posts[i] ? posts[i] + "44" : "rgba(255,255,255,0.08)"}`,
              }}
            >
              {posts[i] && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: posts[i] }} />}
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs" style={{ color: "#64748b" }}>8 posts scheduled this month</span>
        <div className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)" }}>
          Auto-posting ✓
        </div>
      </div>
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────────────────────── */

const TABS = [
  { id: "ads", icon: Megaphone, label: "AI Ads", demo: <AdsDemo /> },
  { id: "agent", icon: Bot, label: "Lead Agent", demo: <AgentDemo /> },
  { id: "dashboard", icon: BarChart3, label: "Dashboard", demo: <DashboardDemo /> },
  { id: "video", icon: Video, label: "Video Ads", demo: <VideoDemo /> },
  { id: "voice", icon: Mic, label: "Voice Clone", demo: <VoiceDemo /> },
  { id: "schedule", icon: CalendarDays, label: "Scheduling", demo: <ScheduleDemo /> },
];

export function FeatureShowcase() {
  const { t } = useI18n();
  const [active, setActive] = useState("ads");
  const current = TABS.find((tab) => tab.id === active)!;

  return (
    <section className="py-24 px-6 relative overflow-hidden" style={{ backgroundColor: "#0a1845" }}>
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 40%, rgba(14,165,233,0.08), transparent 65%)" }}
      />
      {/* Topographic contour lines */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 50%, transparent 20%, rgba(14,165,233,0.8) 21%, transparent 22%), radial-gradient(ellipse at 50% 50%, transparent 35%, rgba(14,165,233,0.6) 36%, transparent 37%), radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(14,165,233,0.5) 51%, transparent 52%), radial-gradient(ellipse at 50% 50%, transparent 65%, rgba(14,165,233,0.4) 66%, transparent 67%)",
          backgroundSize: "120% 120%",
          backgroundPosition: "center",
        }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-3" style={{ color: "#0ea5e9" }}>
            {t("showcase.eyebrow")}
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            {t("showcase.title")}
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: "#7aa3c4" }}>
            {t("showcase.desc")}
          </p>
        </div>

        {/* Main demo area */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Orb */}
          <div className="flex-shrink-0 flex flex-col items-center gap-8">
            <MetallicOrb />
          </div>

          {/* Demo panel */}
          <div className="flex-1 w-full max-w-sm lg:max-w-none">
            <div
              className="p-6 rounded-3xl min-h-[220px]"
              style={{ backgroundColor: "#0c1d50", border: "1px solid rgba(14,165,233,0.15)" }}
            >
              {current.demo}
            </div>
          </div>
        </div>

        {/* Feature tabs */}
        <div className="mt-10 flex flex-wrap justify-center gap-2">
          {TABS.map(({ id, icon: Icon, label }) => {
            const isActive = active === id;
            return (
              <button
                key={id}
                onClick={() => setActive(id)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={{
                  backgroundColor: isActive ? "rgba(14,165,233,0.18)" : "rgba(14,165,233,0.05)",
                  border: `1px solid ${isActive ? "rgba(14,165,233,0.55)" : "rgba(14,165,233,0.15)"}`,
                  color: isActive ? "#38bdf8" : "#7aa3c4",
                  boxShadow: isActive ? "0 0 20px rgba(14,165,233,0.2)" : "none",
                }}
              >
                <Icon size={15} />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes orb-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
